import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Box, Typography, TextField, Button } from '@mui/material';
import JWTService from 'services/JWT';
import useAuth from 'providers/Auth/useAuth';
import logo from 'assets/images/logo.png';
import '../doubleAuthentification.style.scss';
import { useTranslation } from 'react-i18next';

const CODE_LENGTH = 6;
const MAX_ATTEMPTS = 3;
const TIMER_SECONDS = 5 * 60;

const DoubleAuthentificationForm = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const { t } = useTranslation();

  const twoFaToken = searchParams.get('2fa_token');

  const [digits, setDigits] = useState(Array(CODE_LENGTH).fill(''));
  const [attemptsLeft, setAttemptsLeft] = useState(MAX_ATTEMPTS);
  const [secondsLeft, setSecondsLeft] = useState(TIMER_SECONDS);
  const [loading, setLoading] = useState(false);
  const inputsRef = useRef([]);

  const goBackToLogin = (message) => {
    if (message) {
      toast.error(message);
    }
    navigate('/login');
  };

  // Timer 5 minutes
  useEffect(() => {
    if (!twoFaToken) return;

    if (secondsLeft <= 0) {
      goBackToLogin(t('form.doubleAuthentificationExpired'));
      return;
    }

    const interval = setInterval(() => {
      setSecondsLeft((prev) => prev - 1);
    }, 1000);

    return () => clearInterval(interval);
  }, [secondsLeft, twoFaToken, goBackToLogin, t]);

  const formatTime = (seconds) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  const handleChange = (index, value) => {
    const cleanValue = value.replace(/[^0-9]/g, '');
    if (!cleanValue) {
      const updated = [...digits];
      updated[index] = '';
      setDigits(updated);
      return;
    }

    const updated = [...digits];
    updated[index] = cleanValue[cleanValue.length - 1];
    setDigits(updated);

    if (index < CODE_LENGTH - 1) {
      inputsRef.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !digits[index] && index > 0) {
      inputsRef.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData
      .getData('text')
      .replace(/[^0-9]/g, '')
      .slice(0, CODE_LENGTH);
    if (!pasted) return;

    const updated = Array(CODE_LENGTH).fill('');
    pasted.split('').forEach((char, i) => {
      updated[i] = char;
    });
    setDigits(updated);

    const lastFilledIndex = Math.min(pasted.length, CODE_LENGTH - 1);
    inputsRef.current[lastFilledIndex]?.focus();
  };

  const code = digits.join('');
  const isComplete = code.length === CODE_LENGTH;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isComplete || loading) return;

    setLoading(true);

    try {
      const response = await JWTService.verify2FA(twoFaToken, code);
      const user = response.data.data;
      localStorage.setItem('cooperative', user.cooperative.id);

      setUser(user);
      JWTService.setSession(response.data.token, response.data.refresh_token);

      toast.success(t('success.loggedIn'));
      navigate('/');
    } catch (error) {
      const errorType = error?.response?.data?.data?.error_type;
      const errorData = error?.response?.data?.data;
      setDigits(Array(CODE_LENGTH).fill(''));
      inputsRef.current[0]?.focus();

      switch (errorType) {
        case 'INVALID_TOKEN':
          goBackToLogin(t('form.doubleAuthentificationInvalidToken'));
          break;
        case 'EXPIRED_CODE':
          goBackToLogin(t('form.doubleAuthentificationExpiredCode'));
          break;
        case 'INVALID_CODE': {
          const remaining = errorData?.attempts_remaining ?? attemptsLeft - 1;
          setAttemptsLeft(remaining);
          if (remaining <= 0) {
            goBackToLogin(t('form.doubleAuthentificationTooManyAttempts'));
          } else {
            toast.error(t('form.doubleAuthentificationIncorrectCode', { count: remaining }));
          }
          break;
        }
        case 'MAX_ATTEMPTS':
          goBackToLogin(t('form.doubleAuthentificationTooManyAttempts'));
          break;
        default: {
          const remaining = attemptsLeft - 1;
          setAttemptsLeft(remaining);
          if (remaining <= 0) {
            goBackToLogin(t('form.doubleAuthentificationTooManyAttempts'));
          } else {
            toast.error(t('form.doubleAuthentificationIncorrectCode', { count: remaining }));
          }
        }
      }
    } finally {
      setLoading(false);
    }
  };

  if (!twoFaToken) {
    return null;
  }

  return (
    <div className="DoubleAuthentification">
      <div className="form">
        <Box className="container">
          <Typography variant="body2" className="text-center subtitle" color="text.secondary">
            {t('form.doubleAuthentificationSubtitle')}
          </Typography>

          <Box component="form" onSubmit={handleSubmit}>
            <Box className="codeInputs">
              {digits.map((digit, index) => (
                <TextField
                  key={index}
                  inputRef={(el) => {
                    inputsRef.current[index] = el;
                  }}
                  value={digit}
                  autoFocus={index === 0}
                  disabled={loading}
                  onChange={(e) => handleChange(index, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(index, e)}
                  onPaste={handlePaste}
                  className="codeInput"
                  variant="outlined"
                  slotProps={{
                    htmlInput: {
                      inputMode: 'numeric',
                      maxLength: 1,
                      style: { textAlign: 'center' },
                    },
                  }}
                />
              ))}
            </Box>

            <Typography variant="caption" className="text-center timer" color="text.secondary">
              {t('form.doubleAuthentificationTimer', { time: formatTime(secondsLeft) })}
            </Typography>

            <Button type="submit" className="submitButton" disabled={!isComplete || loading}>
              {loading ? t('form.doubleAuthentificationVerifying') : t('common.validate')}
            </Button>
          </Box>
        </Box>
      </div>
    </div>
  );
};

export default DoubleAuthentificationForm;
